import { supabase } from '@/lib/supabase/client';

export interface Client {
  id: string;
  userId: string;
  companyName: string | null;
  taxId: string | null;
  address: string | null;
  city: string | null;
  postalCode: string | null;
  country: string;
  status: 'pending' | 'approved' | 'blocked';
  approvedBy: string | null;
  approvedAt: string | null;
  blockedReason: string | null;
  createdAt: string;
  updatedAt: string;
  userProfile?: {
    fullName: string;
    email: string;
    phone: string | null;
  };
}

export async function getAllClients(): Promise<Client[]> {
  try {
    const { data, error } = await supabase
      .from('clients')
      .select(`
        *,
        user_profiles!clients_user_id_fkey(
          full_name,
          email,
          phone
        )
      `)
      .order('created_at', { ascending: false });

    if (error) throw error;

    return (data || []).map(row => ({
      id: row.id,
      userId: row.user_id,
      companyName: row.company_name,
      taxId: row.tax_id,
      address: row.address,
      city: row.city,
      postalCode: row.postal_code,
      country: row.country,
      status: row.status,
      approvedBy: row.approved_by,
      approvedAt: row.approved_at,
      blockedReason: row.blocked_reason,
      createdAt: row.created_at,
      updatedAt: row.updated_at,
      userProfile: row.user_profiles ? {
        fullName: row.user_profiles.full_name,
        email: row.user_profiles.email,
        phone: row.user_profiles.phone
      } : undefined
    }));
  } catch (error) {
    console.error('Error fetching clients:', error);
    return [];
  }
}

export async function approveClient(clientId: string, adminId: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('clients')
      .update({
        status: 'approved',
        approved_by: adminId,
        approved_at: new Date().toISOString()
      })
      .eq('id', clientId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error approving client:', error);
    return false;
  }
}

export async function blockClient(clientId: string, reason: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('clients')
      .update({
        status: 'blocked',
        blocked_reason: reason
      })
      .eq('id', clientId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error blocking client:', error);
    return false;
  }
}

export async function getClientByUserId(userId: string): Promise<Client | null> {
  try {
    const { data, error } = await supabase
      .from('clients')
      .select('*')
      .eq('user_id', userId)
      .single();

    if (error) throw error;

    return {
      id: data.id,
      userId: data.user_id,
      companyName: data.company_name,
      taxId: data.tax_id,
      address: data.address,
      city: data.city,
      postalCode: data.postal_code,
      country: data.country,
      status: data.status,
      approvedBy: data.approved_by,
      approvedAt: data.approved_at,
      blockedReason: data.blocked_reason,
      createdAt: data.created_at,
      updatedAt: data.updated_at
    };
  } catch (error) {
    console.error('Error fetching client:', error);
    return null;
  }
}